import { useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { signUpload } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { useLatest } from "../../../hooks/useLatest";
import type { PendingUpload } from "../composer/useUploads";

/**
 * Pasting images into the composer: uploads each pasted image (up to 10 MB) and adds it to the pending
 * attachments. Also listens for the Android keyboard's GIF event (`echo:gboard-gif`) and feeds it through
 * the same flow. `onComposerPaste` is what the composer's paste handler calls.
 */
export function useComposerPaste({ setPendingUploads }: { setPendingUploads: Dispatch<SetStateAction<PendingUpload[]>> }) {
  // Paste images directly into the composer
  async function onComposerPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    try {
      const dt = e.clipboardData;
      if (!dt) return;
      const images: File[] = [];
      // Prefer items for accurate detection
      if (dt.items && dt.items.length > 0) {
        for (let i = 0; i < dt.items.length; i++) {
          const it = dt.items[i];
          if (it.kind === 'file') {
            const f = it.getAsFile();
            if (f && f.type && f.type.startsWith('image/')) images.push(f);
          }
        }
      }
      if (images.length === 0 && dt.files && dt.files.length > 0) {
        for (let i = 0; i < dt.files.length; i++) {
          const f = dt.files[i];
          if (f && f.type && f.type.startsWith('image/')) images.push(f);
        }
      }
      if (images.length === 0) return;
      e.preventDefault(); // prevent raw image/text paste into input
      const token = getAuthToken() || '';
      const MAX = 10 * 1024 * 1024; // 10MB
      for (const f of images) {
        if (f.size > MAX) { console.warn('Pasted image too large'); continue; }
        const ext = (f.type && f.type.startsWith('image/')) ? f.type.split('/')[1] : 'png';
        const name = f.name && f.name.trim() ? f.name : `pasted-${Date.now()}.${ext}`;
        try {
          const { url, headers, publicUrl } = await signUpload({ filename: name, contentType: f.type || 'image/png', size: f.size, purpose: "message" }, token);
          await fetch(url, { method: 'PUT', headers, body: f });
          // Chat uploads are private, so an <img> can't load publicUrl; preview from the local file like the file picker does.
          setPendingUploads(prev => [...prev, { url: publicUrl, contentType: f.type || 'image/png', name, size: f.size, localPreviewUrl: URL.createObjectURL(f) }]);
        } catch (err) {
          console.error('Paste upload failed', err);
        }
      }
    } catch { /* noop */ }
  }

  // Fallback handler for Android WebView injections where clipboardData may be stripped.
  // Registered once, so it reaches onComposerPaste (which reads current composer state) through a ref.
  const onComposerPasteRef = useLatest(onComposerPaste);
  useEffect(() => {
    function onNativeGif(ev: Event) {
      const detail = (ev as CustomEvent<{ base64?: string; mime?: string }>).detail;
      if (!detail || !detail.base64) return;
      try {
        const mime = detail.mime && detail.mime.startsWith('image/') ? detail.mime : 'image/gif';
        const byteStr = atob(detail.base64);
        const bytes = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        const file = new File([blob], 'gboard.gif', { type: mime });
        // Reuse the paste flow for uploads
        const fakeEvent = {
          preventDefault() {},
          clipboardData: {
            items: [],
            files: [file],
          },
        } as unknown as React.ClipboardEvent<HTMLTextAreaElement>;
        onComposerPasteRef.current(fakeEvent);
      } catch {}
    }
    window.addEventListener('echo:gboard-gif', onNativeGif);
    return () => window.removeEventListener('echo:gboard-gif', onNativeGif);
  }, [onComposerPasteRef]);
  return { onComposerPaste };
}
